import React, { useState } from 'react';
import { Draggable } from 'react-beautiful-dnd';

import LogicSelector from '@components/common/LogicSelector';
import Label from '@components/common/NumberLabel';
import MetadataRuleItem from '@components/mapping-panel/components/RuleBuilder/MetadataRuleItem';
import { IMetadataRuleRow } from '@components/mapping-panel/components/RuleBuilder/MetadataRuleRow/typed';
import { Prefix } from '@components/mapping-panel/components/RuleBuilder/RuleGroup/styled';
import {
  DeleteButtonWrapper,
  PlaceholderBox,
  TagKeyValueWrapper,
} from '@components/mapping-panel/components/RuleBuilder/RuleRow/styled';
import { Icon } from '@components/metrics-builder/components/MetricList';
import DeleteActiveSvg from '@icons/mapping-panel/delete-active-icon.svg';
import DeleteSvg from '@icons/mapping-panel/delete-icon.svg';
import DragSvg from '@icons/mapping-panel/drag-icon.svg';

const MetadataRuleRow = ({
  id,
  disabled,
  loading,
  index = 0,
  rowLabel = 'A.1',
  isNoRules,
  showLogicOperator,
  itemActiveIndices,
  dimensionName,
  data,
  options,
  onDelete,
  onActive,
  onChange,
  onLogicChange,
  style,
  operatorOptions,
  error,
}: IMetadataRuleRow) => {
  const rowActive = !!itemActiveIndices;

  const [isHover, setHover] = useState<boolean>(false);

  const logicLabelStyle: React.CSSProperties = rowActive
    ? {
        background: '#E6EDFF',
        color: '#0046FF',
        borderColor: 'rgba(0, 0, 0, 0.06)',
      }
    : {};

  const onMouseOver = () => {
    !disabled && setHover(true);
  };

  const onMouseleave = () => {
    !disabled && setHover(false);
  };

  const handleRowDeleteClick = (e) => {
    e.stopPropagation();
    !disabled && onDelete?.();
  };

  const updateActiveRuleRow = (e) => {
    e.stopPropagation();
    !disabled && onActive?.();
  };

  return (
    <Draggable draggableId={rowLabel} index={index}>
      {(provided, snapshot) => {
        const isDragging = snapshot.isDragging;

        // Restrict dragging to vertical axis
        let transform = provided.draggableProps?.style?.transform;

        if (snapshot.isDragging && transform) {
          transform = transform.replace(/\(.+,/, '(0,');
        }

        const dragStyle = {
          ...provided.draggableProps.style,
          transform,
          ...style,
        };

        return (
          <TagKeyValueWrapper
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            ref={provided.innerRef}
            isDragging={isDragging}
            onMouseOver={onMouseOver}
            onMouseLeave={onMouseleave}
            onClick={updateActiveRuleRow}
            style={dragStyle}
          >
            <Prefix>
              {(isHover || isDragging) && !disabled ? (
                <Icon
                  component={DragSvg}
                  style={{ width: '20px', cursor: 'grab' }}
                  {...provided.dragHandleProps}
                />
              ) : (
                <PlaceholderBox />
              )}
              {showLogicOperator ? (
                <LogicSelector
                  disabled={disabled}
                  value="OR"
                  active={rowActive}
                  logicLabelStyle={logicLabelStyle}
                  onChange={onLogicChange}
                />
              ) : null}
            </Prefix>
            <Label value={rowLabel} active={rowActive} />
            <MetadataRuleItem
              id={id}
              disabled={disabled}
              loading={loading}
              active={rowActive}
              isNoRules={isNoRules}
              options={options}
              dimensionName={dimensionName}
              data={data}
              onChange={onChange}
              onActive={onActive}
              operatorOptions={operatorOptions}
            />
            <DeleteButtonWrapper>
              {isHover && !disabled ? (
                <Icon
                  component={DeleteSvg}
                  activeComponent={DeleteActiveSvg}
                  onClick={handleRowDeleteClick}
                />
              ) : (
                <PlaceholderBox style={{ width: '14px' }} />
              )}
            </DeleteButtonWrapper>
          </TagKeyValueWrapper>
        );
      }}
    </Draggable>
  );
};

export default MetadataRuleRow;
